import { DropdownSelect, KpiCard } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import {
  ArrowRightLeft,
  DollarSign,
  Percent,
  PlusCircle,
  ReceiptText,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  OtaPayoutRecord,
  RecordOtaPayoutPayload,
  fetchOtaPayouts,
  recordOtaPayout,
} from '../../../services/accommodationService';
import { apiError, apiSuccess } from '../../../util/notify';
import { fmtAmount, fmtDate } from './reports/reportUtils';

// ─── Record Payout Modal ──────────────────────────────────────────────────────

interface RecordPayoutModalProps {
  propertyId: string;
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}

const PLATFORM_LABELS: Record<string, string> = {
  booking_com: 'Booking.com',
  airbnb: 'Airbnb',
};

const RecordPayoutModal: React.FC<RecordPayoutModalProps> = ({
  propertyId,
  currency,
  onClose,
  onSaved,
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const [platform, setPlatform] = useState<'booking_com' | 'airbnb'>(
    'booking_com',
  );
  const [reference, setReference] = useState('');
  const [grossAmount, setGrossAmount] = useState('');
  const [commissionAmount, setCommissionAmount] = useState('');
  const [netAmount, setNetAmount] = useState('');
  const [receivedAt, setReceivedAt] = useState(today);
  const [isSaving, setIsSaving] = useState(false);

  const handleGrossChange = (v: string) => {
    setGrossAmount(v);
    const gross = parseFloat(v) || 0;
    const comm = parseFloat(commissionAmount) || 0;
    setNetAmount(String((gross - comm).toFixed(2)));
  };

  const handleCommissionChange = (v: string) => {
    setCommissionAmount(v);
    const gross = parseFloat(grossAmount) || 0;
    const comm = parseFloat(v) || 0;
    setNetAmount(String((gross - comm).toFixed(2)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reference.trim()) {
      apiError('Payout reference is required');
      return;
    }
    const gross = parseFloat(grossAmount);
    const commission = parseFloat(commissionAmount) || 0;
    const net = parseFloat(netAmount);
    if (Number.isNaN(gross) || gross <= 0) {
      apiError('Gross amount must be greater than 0');
      return;
    }
    if (Number.isNaN(net) || net < 0) {
      apiError('Net amount cannot be negative');
      return;
    }

    setIsSaving(true);
    try {
      const payload: RecordOtaPayoutPayload = {
        property_id: propertyId,
        platform,
        payout_reference: reference.trim(),
        gross_amount: gross,
        commission_amount: commission,
        net_amount: net,
        currency,
        received_at: new Date(receivedAt).toISOString(),
      };
      await recordOtaPayout(payload);
      apiSuccess('Payout recorded — journal entry posted automatically');
      onSaved();
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to record payout');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">
            Record OTA Payout
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            A journal entry will be posted automatically on save.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <DropdownSelect
            label="Platform"
            value={platform}
            onChange={(v) => setPlatform(v as 'booking_com' | 'airbnb')}
            options={[
              { value: 'booking_com', label: 'Booking.com' },
              { value: 'airbnb', label: 'Airbnb' },
            ]}
          />

          <div>
            <label
              htmlFor="reference"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Payout Reference
            </label>
            <input
              type="text"
              id="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. BDC-20260613-001"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              required
            />
          </div>

          <div>
            <label
              htmlFor="receivedAt"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Date Received
            </label>
            <input
              type="date"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Gross ({currency})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={grossAmount}
                onChange={(e) => handleGrossChange(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Commission ({currency})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={commissionAmount}
                onChange={(e) => handleCommissionChange(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Net ({currency})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={netAmount}
                onChange={(e) => setNetAmount(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSaving ? 'Saving…' : 'Record payout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── OTA Payouts Page ─────────────────────────────────────────────────────────

export const OtaPayoutsPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const currency = property?.currency ?? 'UGX';

  const [payouts, setPayouts] = useState<OtaPayoutRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    if (!property?.id) return;
    setIsLoading(true);
    try {
      const data = await fetchOtaPayouts(property.id);
      setPayouts(data);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      setIsLoading(false);
    }
  }, [property?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const totalGross = payouts.reduce((s, p) => s + p.gross_amount, 0);
  const totalCommission = payouts.reduce((s, p) => s + p.commission_amount, 0);
  const totalNet = payouts.reduce((s, p) => s + p.net_amount, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">OTA Payouts</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Booking.com and Airbnb payouts. A journal entry (Dr Bank + Dr
            Commission → Cr OTA Receivables) is posted automatically when a
            payout is recorded.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          disabled={!property?.id}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <PlusCircle size={14} />
          Record payout
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard
          title="Total Gross"
          value={`${currency} ${fmtAmount(totalGross)}`}
          sub={`${payouts.length} payout(s)`}
          icon={DollarSign}
          accent="bg-pos-primary"
        />
        <KpiCard
          title="Total Commission"
          value={`${currency} ${fmtAmount(totalCommission)}`}
          sub="deducted by OTA"
          icon={Percent}
          accent="bg-red-500"
        />
        <KpiCard
          title="Net Received"
          value={`${currency} ${fmtAmount(totalNet)}`}
          sub="banked after commission"
          icon={ArrowRightLeft}
          accent="bg-pos-secondary"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-5 py-3">
          <h4 className="text-sm font-semibold text-gray-900">
            Payout history
          </h4>
        </div>
        {isLoading ? (
          <div className="flex h-28 items-center justify-center text-sm text-gray-400">
            Loading…
          </div>
        ) : payouts.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center gap-2 text-center">
            <ReceiptText size={28} className="text-gray-300" />
            <p className="text-sm text-gray-400">No OTA payouts recorded yet</p>
          </div>
        ) : (
          <div className="max-h-[32rem] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                <tr>
                  {[
                    'Date Received',
                    'Platform',
                    'Reference',
                    `Gross (${currency})`,
                    `Commission (${currency})`,
                    `Net (${currency})`,
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payouts.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 font-medium text-gray-900">
                      {fmtDate(p.received_at.slice(0, 10))}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {PLATFORM_LABELS[p.platform] ?? p.platform}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                      {p.payout_reference}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                      {fmtAmount(p.gross_amount)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-red-600">
                      {p.commission_amount > 0
                        ? fmtAmount(p.commission_amount)
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-green-700">
                      {fmtAmount(p.net_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-2.5 text-xs font-semibold text-gray-600"
                  >
                    Total ({payouts.length})
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-900">
                    {fmtAmount(totalGross)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-red-600">
                    {fmtAmount(totalCommission)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm font-bold text-green-700">
                    {fmtAmount(totalNet)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {showModal && property?.id && (
        <RecordPayoutModal
          propertyId={property.id}
          currency={currency}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
    </div>
  );
};
